<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"

  import { onDestroy, onMount, untrack } from "svelte"
  import type { OrderTypesBinding } from "./_logic/binding"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Table, {
    type TableFilterValues,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import { openOverlay, closeOverlayById } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiDelete, mdiPencil, mdiPlus, mdiRestore } from "@mdi/js"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import OrderTypeTableCell from "./_modules/order-type-table-cell"
  import OrderTypeOverlay from "./_modules/order-type-overlay"
  import {
    filterOrderTypeRows,
    orderTypeColumns,
    orderTypeFilter,
    toOrderTypeRows,
    type OrderTypeRow,
  } from "./_shared/_logic/table-model"
  import {
    SETTINGS_ACTION_TOOLTIP_CLASS,
    settingsActionTooltips,
  } from "./_logic/settingsActionTooltips"

  const { binding } = $props<{ binding: OrderTypesBinding }>()
  const local = new AbortController()
  const signal = AbortSignal.any([untrack(() => binding.signal), local.signal])
  const ownedOverlays = new Set<string>()
  let available = $state(true)
  let pending = $state(false)
  let loadError = $state(false)
  let readGeneration = 0
  const current = () => !signal.aborted && binding.isCurrent()
  const canEdit = $derived(available && binding.canManage)
  const invalidate = () => {
    available = false
    readGeneration += 1
    orderTypes = []
    totalCount = null
    ownedOverlays.forEach(closeOverlayById)
    ownedOverlays.clear()
  }
  onMount(() => {
    signal.addEventListener("abort", invalidate)

    if (!current()) invalidate()

    return () => signal.removeEventListener("abort", invalidate)
  })
  onDestroy(() => {
    local.abort()
    invalidate()
  })
  let orderTypes = $state<OrderType[]>([])
  let tableRefresh = $state(0)
  let refreshTable: (() => void | Promise<void>) | null = null
  const numberFormatter = new Intl.NumberFormat("de-DE")
  let totalCount = $state<number | null>(null)
  const titleWithCount = $derived.by(
    () => `Auftragstypen (${numberFormatter.format(totalCount ?? 0)})`,
  )

  const getRows: TableGetRows<TableFilterValues> = async (params) => {
    const { filters, limit, offset } = params
    const sort =
      (params as { sort?: Parameters<typeof getSortedTablePage<OrderTypeRow>>[0]["sort"] }).sort ??
      null

    if (!current()) return { rows: [], total: 0 }

    const generation = ++readGeneration
    let result: OrderType[]
    try {
      result = await binding.list(signal)
    } catch {
      if (current() && generation === readGeneration) loadError = true

      return { rows: [], total: 0 }
    }

    if (!current() || generation !== readGeneration) return { rows: [], total: 0 }

    loadError = false
    orderTypes = result
    const filtered = filterOrderTypeRows(toOrderTypeRows(orderTypes), filters)

    return getSortedTablePage({
      rows: filtered,
      columns: orderTypeColumns,
      sort,
      limit,
      offset,
    })
  }

  const upsertType = (updated: OrderType) => {
    if (!current()) return

    const existingIndex = orderTypes.findIndex((entry) => entry.id === updated.id)

    if (existingIndex >= 0) {
      const clone = [...orderTypes]
      clone[existingIndex] = updated
      orderTypes = clone
    } else {
      orderTypes = [updated, ...orderTypes]
    }

    refreshTable?.()
  }

  const openCreateOverlay = () => {
    if (!current() || !canEdit || pending || ownedOverlays.size) return

    let beforeClose: (() => boolean | Promise<boolean>) | null = null

    const id = openOverlay({
      title: "Auftragstyp anlegen",
      content: OrderTypeOverlay,
      closeOnParentOpen: true,
      properties: {
        save: binding.create,
        signal,
        isCurrent: current,
        setBeforeClose: (guard: typeof beforeClose) => {
          beforeClose = guard
        },
        onSaved: (created: OrderType) => {
          upsertType(created)
        },
      },
      onClose: () => {
        ownedOverlays.delete(id)
      },
      beforeClose: () => beforeClose?.() ?? true,
    })
    ownedOverlays.add(id)
  }

  const restoreDefaults = async () => {
    if (!current() || !canEdit || pending || ownedOverlays.size) return

    pending = true

    await newApproval({
      title: "Defaults wiederherstellen",
      description:
        "Default-Auftragstypen werden ergänzt und auf die Standardwerte zurückgesetzt. Eigene Auftragstypen werden entfernt.",
      approvalLabel: "Wiederherstellen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        if (!current() || !canEdit) return

        try {
          const result = await binding.restore(signal)

          if (!current()) return

          refreshTable?.()
          newNotification({
            class: "success",
            html: `Defaults wiederhergestellt. Neu: ${result.created}, zurückgesetzt: ${result.updated}.`,
          })
        } catch {
          if (!current()) return

          newNotification({
            class: "error",
            html: "Defaults konnten nicht wiederhergestellt werden.",
          })
        }
      },
    })
    pending = false
  }

  const openEditOverlay = (orderType: OrderType) => {
    if (!current() || !canEdit || pending || ownedOverlays.size) return

    let beforeClose: (() => boolean | Promise<boolean>) | null = null

    const id = openOverlay({
      title: "Auftragstyp bearbeiten",
      content: OrderTypeOverlay,
      closeOnParentOpen: true,
      properties: {
        save: (payload: OrderTypeUpdatePayload, requestSignal: AbortSignal) =>
          binding.update(orderType.id, payload, requestSignal),
        signal,
        isCurrent: current,
        setBeforeClose: (guard: typeof beforeClose) => {
          beforeClose = guard
        },
        orderType,
        onSaved: (updated: OrderType) => {
          upsertType(updated)
        },
      },
      onClose: () => {
        ownedOverlays.delete(id)
      },
      beforeClose: () => beforeClose?.() ?? true,
    })
    ownedOverlays.add(id)
  }

  const removeOrderType = async (orderType: OrderType) => {
    if (!current() || !canEdit || pending || ownedOverlays.size || orderType.is_protected) return

    pending = true

    await newApproval({
      title: "Auftragstyp löschen",
      description: `Auftragstyp "${orderType.internal_label}" wirklich löschen? Neue Aufträge können diesen Typ danach nicht mehr auswählen.`,
      approvalLabel: "Ja, löschen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        if (!current() || !canEdit) return

        try {
          await binding.remove(orderType.id, signal)

          if (!current()) return

          orderTypes = orderTypes.filter((entry) => entry.id !== orderType.id)
          refreshTable?.()
          newNotification({ class: "success", html: "Auftragstyp wurde gelöscht." })
        } catch {
          if (!current()) return

          newNotification({ class: "error", html: "Auftragstyp konnte nicht gelöscht werden." })
        }
      },
    })
    pending = false
  }
</script>

{#if available}
<div class="section-header">
  <SectionTitle title={titleWithCount} brightBackground={true} helpModalTextId="orderTypes">
    {#snippet buttonSnippet()}
      <div class="section-actions">
        {#if canEdit}
          <CTA
              type="secondaryButton"
              content=""
              ariaLabel="Defaults wiederherstellen"
              disabled={pending}
              tooltipContent={settingsActionTooltips.restoreOrderTypeDefaults}
              tooltipClassName={SETTINGS_ACTION_TOOLTIP_CLASS}
              onClick={restoreDefaults}
            >
            {#snippet iconSnippet()}
              <Icon path={mdiRestore} />
            {/snippet}
          </CTA>
          <CTA
              type="primaryButton"
              content=""
              ariaLabel="Auftragstyp anlegen"
              disabled={pending}
              tooltipContent="Auftragstyp anlegen"
              onClick={openCreateOverlay}
            >
            {#snippet iconSnippet()}
              <Icon path={mdiPlus} />
            {/snippet}
          </CTA>
        {/if}
      </div>
    {/snippet}
  </SectionTitle>
</div>

{#if loadError}<p role="alert">Auftragstypen konnten nicht geladen werden.</p><button
      onclick={() => refreshTable?.()}>Erneut laden</button
    >{/if}

<Table
    rowActions={{
      resolve: (row, rowIndex) =>
        buildTableRowActionSet(
          canEdit
            ? (((row) => {
                const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] =
                  []

                const typeRow = row as OrderTypeRow

                actions.push({
                  id: "action-0",
                  label: "Auftragstyp bearbeiten",
                  disabled: pending,
                  onClick: () => openEditOverlay(typeRow.orderType),
                  icon: mdiPencil,
                  tooltip: "Auftragstyp bearbeiten",
                })

                if (!typeRow.orderType.is_protected)
                  actions.push({
                    id: "action-1",
                    label: "Löschen",
                    disabled: pending,
                    onClick: () => removeOrderType(typeRow.orderType),
                    icon: mdiDelete,
                    tooltip: settingsActionTooltips.deleteOrderType,
                    tone: "danger",
                  })

                return actions
              })(row) ?? [])
            : [],
          { primaryId: "action-0", inlineIds: [] },
        ),
    }}
    columns={orderTypeColumns}
    {getRows}
    refreshKey={tableRefresh}
    theme="light"
    mode="table"
    showActions={canEdit}
    actionsHeaderLabel="Aktionen"
    urlStateId={`kehrblatt-order-types-${binding.organizationId}`}
    filter={orderTypeFilter}
    onRowsLoaded={(result) => {
      totalCount = typeof result?.total === "number" ? result.total : (result?.rows?.length ?? 0)
    }}
    setRefreshFunction={(fn) => (refreshTable = fn)}
  >
  {#snippet tableCell({ row, column })}
    {@const typeRow = row as OrderTypeRow}
    <OrderTypeTableCell row={typeRow} {column} />
  {/snippet}

  {#snippet empty()}
    <div class="empty-state">
      <p style="color: var(--text-invers-100)">Keine Auftragstypen vorhanden.</p>
    </div>
  {/snippet}
</Table>
{:else}<p role="status">Zugang zu Auftragstypen nicht bestaetigt.</p>{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .section-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 0;
  }
</style>
